/**
 * Concurrency control and the global circuit breaker (ARCHITECTURE.md §9).
 *
 * Graph throttles per service, and Outlook endpoints are considerably stricter
 * than OneDrive — so limits are per service rather than global. On sustained
 * 429s the breaker trips and drops every service to a single in-flight request,
 * because continuing to hammer a throttled tenant only extends the backoff.
 */

export type ServiceName = "outlook" | "drive" | "default";

/** §9 starting points. Exposed as a user setting via setConcurrency(). */
const DEFAULT_LIMITS: Record<ServiceName, number> = {
    outlook: 4,
    drive: 8,
    default: 4
};

/** Trip after this many throttle responses inside the window. */
const BREAKER_THRESHOLD = 5;
const BREAKER_WINDOW_MS = 30_000;
/** Time without a throttle response before normal concurrency is restored. */
const BREAKER_RECOVERY_MS = 60_000;

class Semaphore {
    private active = 0;
    private waiters: (() => void)[] = [];

    private limit: number;

    constructor(limit: number) {
        this.limit = limit;
    }

    async acquire(): Promise<void> {
        if (this.active < this.limit) {
            this.active += 1;
            return;
        }
        // The permit is accounted for by drain() at the moment it hands one
        // out, not here — see the note there.
        await new Promise<void>((resolve) => this.waiters.push(resolve));
    }

    release(): void {
        this.active -= 1;
        this.drain();
    }

    setLimit(limit: number): void {
        this.limit = Math.max(1, limit);
        this.drain();
    }

    private drain(): void {
        while (this.active < this.limit && this.waiters.length > 0) {
            // Count the permit synchronously. A resumed waiter only continues on
            // a later microtask, so incrementing there would leave `active`
            // stale for the rest of this loop and admit past the limit.
            this.active += 1;
            this.waiters.shift()?.();
        }
    }
}

const configured: Record<ServiceName, number> = { ...DEFAULT_LIMITS };

const semaphores: Record<ServiceName, Semaphore> = {
    outlook: new Semaphore(DEFAULT_LIMITS.outlook),
    drive: new Semaphore(DEFAULT_LIMITS.drive),
    default: new Semaphore(DEFAULT_LIMITS.default)
};

let throttleTimes: number[] = [];
let breakerTrippedAt: number | null = null;

function services(): ServiceName[] {
    return ["outlook", "drive", "default"];
}

function applyLimits(): void {
    for (const service of services()) {
        semaphores[service].setLimit(breakerTrippedAt === null ? configured[service] : 1);
    }
}

/** Overrides the per-service concurrency cap (the advanced user setting, §11). */
export function setConcurrency(service: ServiceName, limit: number): void {
    configured[service] = Math.max(1, limit);
    applyLimits();
}

export function getConcurrency(service: ServiceName): number {
    return configured[service];
}

export function isBreakerTripped(): boolean {
    return breakerTrippedAt !== null;
}

/**
 * Records a 429/503. Trips the breaker once they cluster, which forces every
 * service down to one in-flight request until things go quiet.
 */
export function recordThrottle(): void {
    const now = Date.now();
    throttleTimes = throttleTimes.filter((t) => now - t < BREAKER_WINDOW_MS);
    throttleTimes.push(now);

    if (throttleTimes.length >= BREAKER_THRESHOLD && breakerTrippedAt === null) {
        breakerTrippedAt = now;
        applyLimits();
    } else if (breakerTrippedAt !== null) {
        // Still being throttled — restart the recovery clock.
        breakerTrippedAt = now;
    }
}

/** Called after a success; restores concurrency once the tenant has settled. */
export function recordSuccess(): void {
    if (breakerTrippedAt !== null && Date.now() - breakerTrippedAt > BREAKER_RECOVERY_MS) {
        breakerTrippedAt = null;
        throttleTimes = [];
        applyLimits();
    }
}

/** Runs `fn` inside the concurrency budget for `service`. */
export async function withSlot<T>(service: ServiceName, fn: () => Promise<T>): Promise<T> {
    const semaphore = semaphores[service];
    await semaphore.acquire();
    try {
        return await fn();
    } finally {
        semaphore.release();
    }
}

export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            signal?.removeEventListener("abort", onAbort);
            resolve();
        }, ms);

        const onAbort = () => {
            clearTimeout(timer);
            reject(new DOMException("Aborted", "AbortError"));
        };

        signal?.addEventListener("abort", onAbort, { once: true });
    });
}

/**
 * Backoff for when a throttle response carries no Retry-After. Exponential with
 * jitter, so a fleet of parallel requests does not retry in lockstep (§9).
 */
export function backoffDelay(attempt: number): number {
    const base = Math.min(1000 * 2 ** attempt, 60_000);
    return Math.round(base * (0.5 + Math.random() * 0.5));
}

/** Reset hook for tests and for starting a fresh run. */
export function resetThrottleState(): void {
    throttleTimes = [];
    breakerTrippedAt = null;
    Object.assign(configured, DEFAULT_LIMITS);
    applyLimits();
}
